
import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  VariantUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/*
 * ONLYONE
 * Maximum 1 item per Product ID, including all variants.
 * Applies when product-form-component has:
 * data-onlyone="true"
 * data-product-id="{{ product.id }}"
 */

const onlyonePendingProducts = new Set();

async function checkOnlyoneCart(productId, quantity) {
  if (!productId || quantity !== 1) return false;

  const root = window.Shopify?.routes?.root || '/';

  const response = await fetch(`${root}cart.js`, {
    headers: {
      Accept: 'application/json',
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error('Unable to check cart');
  }

  const cart = await response.json();

  const total = cart.items.reduce((sum, item) => {
    if (String(item.product_id) === String(productId)) {
      return sum + Number(item.quantity || 0);
    }

    return sum;
  }, 0);

  return total + quantity <= 1;
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  #animationTimeout;
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }

    if (this.#cleanupTimeout) {
      clearTimeout(this.#cleanupTimeout);
    }

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(
      '.header-actions__cart-icon'
    );

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = document.createElement('fly-to-cart');

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${image})`
    );

    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }

    if (this.#cleanupTimeout) {
      clearTimeout(this.#cleanupTimeout);
    }

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define(
    'add-to-cart-component',
    AddToCartComponent
  );
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {AddToCartComponent | undefined} addToCartButtonContainer
 * @property {HTMLElement | undefined} addToCartTextError
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer
 * @property {HTMLElement} liveRegion
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();
  #timeout;
  #submitting = false;
  #onlyoneCheckId = 0;
  #onlyoneRefreshTimer;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    const target = this.closest(
      '.shopify-section, dialog, product-card'
    );

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );

    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );

    // Refresh after cart changes, including changes made in the drawer.
    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#scheduleOnlyoneRefresh,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.cartAdd,
      this.#scheduleOnlyoneRefresh,
      { signal }
    );

    window.addEventListener(
      'pageshow',
      this.#scheduleOnlyoneRefresh,
      { signal }
    );

    document.addEventListener(
      'visibilitychange',
      this.#scheduleOnlyoneRefresh,
      { signal }
    );

    this.#refreshOnlyoneButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#onlyoneCheckId++;
    clearTimeout(this.#onlyoneRefreshTimer);
  }

  #scheduleOnlyoneRefresh = () => {
    clearTimeout(this.#onlyoneRefreshTimer);

    this.#onlyoneRefreshTimer = setTimeout(
      () => this.#refreshOnlyoneButton(),
      180
    );
  };

  async #refreshOnlyoneButton() {
    const button =
      this.refs.addToCartButtonContainer?.refs.addToCartButton;

    if (!button) return;

    const requestId = ++this.#onlyoneCheckId;

    if (this.dataset.onlyone !== 'true') {
      button.removeAttribute('data-onlyone-blocked');

      // Do not override unavailable/variant-selection disabled states.
      if (button.dataset.onlyoneDisabled === 'true') {
        button.disabled = false;
        delete button.dataset.onlyoneDisabled;
      }

      return;
    }

    const productId = String(this.dataset.productId || '');

    if (!productId) return;

    try {
      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart.js`, {
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error('Cart check failed');
      }

      const cart = await response.json();

      if (
        requestId !== this.#onlyoneCheckId ||
        !this.isConnected
      ) {
        return;
      }

      const exists = cart.items.some(
        (item) =>
          String(item.product_id) === productId &&
          Number(item.quantity) > 0
      );

      if (exists) {
        button.disabled = true;
        button.dataset.onlyoneDisabled = 'true';
        button.dataset.onlyoneBlocked = 'true';
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('data-onlyone-blocked');
        button.removeAttribute('aria-disabled');

        if (button.dataset.onlyoneDisabled === 'true') {
          button.disabled = false;
          delete button.dataset.onlyoneDisabled;
        }
      }
    } catch (error) {
      console.error('ONLYONE button refresh:', error);

      // Fail closed for tagged products until cart status can be checked.
      if (requestId === this.#onlyoneCheckId) {
        button.disabled = true;
        button.dataset.onlyoneDisabled = 'true';
      }
    }
  }

  /**
   * Handles product form submission.
   *
   * @param {Event} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (this.#submitting) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    const addToCartButton =
      this.refs.addToCartButtonContainer?.refs.addToCartButton;

    if (addToCartButton?.disabled) return;

    const form = this.querySelector('form');

    if (!form) {
      throw new Error('Product form element missing');
    }

    const formData = new FormData(form);

    const isOnlyone = this.dataset.onlyone === 'true';
    const productId = String(this.dataset.productId || '');

    const quantity =
      Number(formData.get('quantity')) ||
      Number(this.dataset.quantityDefault) ||
      1;

    let onlyoneLocked = false;

    this.#submitting = true;

    try {
      /*
       * ONLYONE VALIDATION
       *
       * Block:
       * - Quantity greater than 1
       * - Another variant of the same product
       * - Adding the same product again
       * - Repeated clicks while request is running
       */

      if (isOnlyone) {
        if (!productId) {
          console.error(
            'ONLYONE: Missing data-product-id'
          );

          return;
        }

        if (onlyonePendingProducts.has(productId)) {
          return;
        }

        onlyonePendingProducts.add(productId);
        onlyoneLocked = true;

        const allowed = await checkOnlyoneCart(
          productId,
          quantity
        );

        if (!allowed) {
          const message =
            'Mỗi đơn hàng chỉ được mua tối đa 01 sản phẩm';

          window.dispatchEvent(
            new CartErrorEvent(this.id, message)
          );

          return;
        }
      }

      /*
       * ORIGINAL ADD TO CART FLOW
       */

      const cartItemsComponents =
        document.querySelectorAll('cart-items-component');

      const cartItemComponentsSectionIds = [];

      cartItemsComponents.forEach((item) => {
        if (
          item instanceof HTMLElement &&
          item.dataset.sectionId
        ) {
          cartItemComponentsSectionIds.push(
            item.dataset.sectionId
          );
        }
      });

      if (cartItemComponentsSectionIds.length) {
        formData.append(
          'sections',
          cartItemComponentsSectionIds.join(',')
        );
      }

      const fetchCfg = fetchConfig('javascript', {
        body: formData,
      });

      const response = await fetch(
        Theme.routes.cart_add_url,
        {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: 'text/html',
          },
        }
      );

      const result = await response.json();

      if (result.status) {
        window.dispatchEvent(
          new CartErrorEvent(this.id, result.message)
        );

        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent = result.message;
          } else {
            addToCartTextError.appendChild(
              document.createTextNode(result.message)
            );
          }

          this.#setLiveRegionText(result.message);

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);
        }

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: quantity,
            productId: this.dataset.productId,
          })
        );

        return;
      }

      const id = formData.get('id');

      if (!id) {
        throw new Error('Form ID is required');
      }

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      if (addToCartButton) {
        const addedTextElement =
          addToCartButton.querySelector(
            '.add-to-cart-text--added'
          );

        const addedText =
          addedTextElement?.textContent?.trim() ||
          Theme.translations.added;

        this.#setLiveRegionText(addedText);

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, 5000);
      }

      this.dispatchEvent(
        new CartAddEvent({}, id.toString(), {
          source: 'product-form-component',
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: result.sections,
        })
      );

      this.#scheduleOnlyoneRefresh();
    } catch (error) {
      console.error('Product form error:', error);
    } finally {
      if (onlyoneLocked) {
        onlyonePendingProducts.delete(productId);
      }

      this.#submitting = false;

      cartPerformance.measureFromEvent(
        'add:user-action',
        event
      );
    }
  }

  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;

    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;

    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId =
        event.detail.data.newProduct.id;
    } else if (
      event.detail.data.productId !==
      this.dataset.productId
    ) {
      return;
    }

    const {
      variantId,
      addToCartButtonContainer,
    } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton;

    const newAddToCartButton =
      event.detail.data.html.querySelector(
        '[ref="addToCartButton"]'
      );

    if (!currentAddToCartButton) return;

    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      addToCartButtonContainer.disable();

      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        'hidden',
        'true'
      );
    } else {
      addToCartButtonContainer.enable();

      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute(
        'hidden'
      );
    }

    if (newAddToCartButton) {
      morph(
        currentAddToCartButton,
        newAddToCartButton
      );
    }

    variantId.value =
      event.detail.resource.id ?? '';

    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media
          ?.preview_image?.src;

      if (productVariantMedia) {
        addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    /*
     * Refresh ONLYONE status when the theme
     * switches to another product.
     */
    const newProductForm =
      event.detail.data.html.querySelector(
        'product-form-component'
      );

    if (newProductForm) {
      this.dataset.onlyone =
        newProductForm.dataset.onlyone || 'false';
    }

    this.#scheduleOnlyoneRefresh();
  };

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define(
    'product-form-component',
    ProductFormComponent
  );
}

/**
 * Fly to cart animation
 */
class FlyToCart extends HTMLElement {
  source;
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();

    const sourceRect =
      this.source.getBoundingClientRect();

    const destinationRect =
      this.destination.getBoundingClientRect();

    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };

    const startPoint = {
      x:
        sourceRect.left +
        sourceRect.width / 2 -
        offset.x,
      y:
        sourceRect.top +
        sourceRect.height / 2 -
        offset.y,
    };

    const endPoint = {
      x:
        destinationRect.left +
        destinationRect.width / 2 -
        offset.x,
      y:
        destinationRect.top +
        destinationRect.height / 2 -
        offset.y,
    };

    const controlPoint1 = {
      x: startPoint.x,
      y: startPoint.y - 200,
    };

    const controlPoint2 = {
      x: endPoint.x - 300,
      y: endPoint.y - 100,
    };

    let startTime = null;
    const duration = 600;

    this.style.opacity = '1';

    const animate = (currentTime) => {
      if (!startTime) {
        startTime = currentTime;
      }

      const elapsed = currentTime - startTime;

      const progress = Math.min(
        elapsed / duration,
        1
      );

      const position = bezierPoint(
        progress,
        startPoint,
        controlPoint1,
        controlPoint2,
        endPoint
      );

      this.style.setProperty(
        '--x',
        `${position.x}px`
      );

      this.style.setProperty(
        '--y',
        `${position.y}px`
      );

      const scale = 1 - progress * 0.5;

      this.style.setProperty(
        '--scale',
        `${scale}`
      );

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';

        onAnimationEnd(this, () => {
          this.remove();
        });
      }
    };

    this.style.setProperty(
      '--x',
      `${startPoint.x}px`
    );

    this.style.setProperty(
      '--y',
      `${startPoint.y}px`
    );

    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);

  const bX =
    3 * (p2.x - p1.x) - cX;

  const aX =
    p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);

  const bY =
    3 * (p2.y - p1.y) - cY;

  const aY =
    p3.y - p0.y - cY - bY;

  const x =
    aX * Math.pow(t, 3) +
    bX * Math.pow(t, 2) +
    cX * t +
    p0.x;

  const y =
    aY * Math.pow(t, 3) +
    bY * Math.pow(t, 2) +
    cY * t +
    p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
